import React from "react";
import { usePolling } from "../hooks/usePolling";
import { fetchWeather, fetchTraffic, fetchOBU } from "../mock/context.mock";
import TimeFilterControl from "./TimeFilterControl";

const WEATHER_MAP = {
  clear: { icon: "☀️", label: "Clear" },
  partly_cloudy: { icon: "⛅", label: "Partly cloudy" },
  cloudy: { icon: "☁️", label: "Cloudy" },
  light_rain: { icon: "🌦️", label: "Light rain" },
  moderate_rain: { icon: "🌧️", label: "Moderate rain" },
  heavy_rain: { icon: "⛈️", label: "Heavy rain" },
  fog: { icon: "🌫️", label: "Fog" },
  snow: { icon: "❄️", label: "Snow" },
};
const WEATHER_FALLBACK = { icon: "🌡️", label: "Weather" };

const Skeleton = () => (
  <div
    aria-busy="true"
    aria-label="Loading"
    className="h-[30px] w-44 animate-pulse rounded-full bg-gray-200 dark:bg-navy-700"
  />
);

const UnavailablePill = ({ label }) => (
  <div className="flex h-[30px] items-center gap-1.5 rounded-full border border-yellow-300 bg-yellow-50 px-3 dark:border-yellow-700 dark:bg-yellow-900/20">
    <span aria-hidden="true" className="text-sm">
      ⚠️
    </span>
    <span className="whitespace-nowrap text-xs font-medium text-yellow-700 dark:text-yellow-400">
      {label} · Unavailable
    </span>
  </div>
);

const BasePill = ({ children, className = "" }) => (
  <div
    className={`flex h-[30px] items-center gap-1.5 rounded-full border px-3 ${className}`}
  >
    {children}
  </div>
);

const WeatherPill = () => {
  const { data, loading, error } = usePolling(fetchWeather, 10_000);

  if (loading) return <Skeleton />;
  if (error || !data) return <UnavailablePill label="Weather" />;

  const { icon, label } = WEATHER_MAP[data.condition_code] ?? WEATHER_FALLBACK;

  return (
    <BasePill className="border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800">
      <span aria-hidden="true" className="text-sm">
        {icon}
      </span>
      <span className="whitespace-nowrap text-xs font-medium text-gray-700 dark:text-gray-300">
        {label} · {data.temperature_c}°C · Vis. {data.visibility_m}m
      </span>
    </BasePill>
  );
};

const TrafficPill = ({ onZoomToLocation }) => {
  const { data, loading, error } = usePolling(fetchTraffic, 60_000);

  if (loading) return <Skeleton />;
  if (error || !data) return <UnavailablePill label="Traffic" />;

  const isCongestion = data.status === "congestion";

  const handleClick = () => {
    if (isCongestion && data.location) {
      onZoomToLocation?.(data.location.lat, data.location.lng);
    }
  };

  if (isCongestion) {
    return (
      <button
        onClick={handleClick}
        aria-label={`Traffic: ${data.description}. Click to zoom to congestion location.`}
        className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 transition-colors hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/30 dark:hover:bg-amber-900/50"
      >
        <span aria-hidden="true" className="text-sm">
          🟠
        </span>
        <span className="whitespace-nowrap text-xs font-medium text-amber-700 dark:text-amber-400">
          {data.description}
        </span>
      </button>
    );
  }

  return (
    <BasePill className="border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800">
      <span aria-hidden="true" className="text-sm">
        🟢
      </span>
      <span className="whitespace-nowrap text-xs font-medium text-gray-700 dark:text-gray-300">
        {data.description}
      </span>
    </BasePill>
  );
};

const OBUPill = ({ onActivateLiveView }) => {
  const { data, loading, error } = usePolling(fetchOBU, 15_000);

  if (loading) return <Skeleton />;
  if (error || !data) return <UnavailablePill label="OBU" />;

  const text = data.active
    ? `${data.vehicle_count} vehicle${
        data.vehicle_count !== 1 ? "s" : ""
      } · last ping ${data.last_ping_seconds}s ago`
    : "No active probe · last campaign";

  if (data.active) {
    return (
      <button
        onClick={() => onActivateLiveView?.()}
        aria-label={`OBU: ${text}. Click to activate Live View.`}
        className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-full border border-green-300 bg-green-50 px-3 transition-colors hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50"
      >
        <span aria-hidden="true" className="text-sm">
          🟢
        </span>
        <span className="whitespace-nowrap text-xs font-medium text-green-700 dark:text-green-400">
          {text}
        </span>
      </button>
    );
  }

  return (
    <BasePill className="border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800">
      <span aria-hidden="true" className="text-sm">
        ⚫
      </span>
      <span className="whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-400">
        {text}
      </span>
    </BasePill>
  );
};

const ContextBar = ({ onZoomToLocation, onActivateLiveView }) => (
  <div
    role="region"
    aria-label="Live context"
    className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-sm dark:border-white/10 dark:bg-navy-800"
  >
    <WeatherPill />
    <TrafficPill onZoomToLocation={onZoomToLocation} />
    <OBUPill onActivateLiveView={onActivateLiveView} />
    <div className="ml-auto">
      <TimeFilterControl />
    </div>
  </div>
);

export default ContextBar;
